import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { buildApiUrl, fetchJson, getErrorMessage, readResponseData } from '../lib/api';

const AuthContext = createContext(null);

function mapSessionUser(data) {
  return {
    email: data?.email || '',
    rol: data?.rol || '',
    usuarioId: data?.usuarioId ?? data?.userId ?? null,
    clienteId: data?.clienteId ? Number(data.clienteId) : null,
    tiendaNubeId: data?.tiendaNubeId ? Number(data.tiendaNubeId) : null,
  };
}

function readStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [user, setUser] = useState(() => readStoredUser());
  const [authReady, setAuthReady] = useState(() => !localStorage.getItem('token'));
  const initialTokenRef = useRef(token);

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  useEffect(() => {
    const initialToken = initialTokenRef.current;
    if (!initialToken) {
      setAuthReady(true);
      return;
    }

    let cancelled = false;

    const verifySession = async () => {
      try {
        const response = await fetch(buildApiUrl('/auth/me'), {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${initialToken}`,
          },
        });
        const data = await readResponseData(response);
        if (!response.ok || !data?.ok) {
          throw new Error(getErrorMessage(data, 'Sesion invalida'));
        }

        if (!cancelled) {
          setUser({
            email: data.email || '',
            rol: data.rol || '',
            usuarioId: data.userId ? Number(data.userId) : null,
            clienteId: data.clienteId ? Number(data.clienteId) : null,
            tiendaNubeId: data.tiendaNubeId ? Number(data.tiendaNubeId) : null,
          });
        }
      } catch {
        if (!cancelled) {
          setToken(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setAuthReady(true);
        }
      }
    };

    verifySession();

    return () => {
      cancelled = true;
    };
  }, []);

  const setSession = (data) => {
    if (!data?.token) {
      return data;
    }

    setToken(data.token);
    setUser(mapSessionUser(data));
    setAuthReady(true);
    return data;
  };

  const login = async (email, password, installToken = null) => {
    const data = await fetchJson('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, installToken }),
    });

    setSession(data);
    return data;
  };

  const register = async (payload, installToken = null) => {
    const data = await fetchJson('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ ...payload, installToken }),
    });

    setSession(data);
    return data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setAuthReady(true);
  };

  const authFetch = async (path, options = {}) => {
    const headers = {
      ...(options.headers || {}),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (options.body != null && typeof options.body === 'string' && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(buildApiUrl(path), {
      ...options,
      headers,
    });

    // Si la sesión venció o fue firmada con una configuración anterior,
    // limpiar el estado local para que el portal vuelva al login en lugar de
    // dejar la interfaz intentando operar con un Bearer inválido.
    if (response.status === 401 && token) {
      setToken(null);
      setUser(null);
      setAuthReady(true);
    }

    return response;
  };

  const authRequestJson = async (path, options = {}) => {
    const response = await authFetch(path, options);
    const data = await readResponseData(response);

    if (!response.ok) {
      const error = new Error(getErrorMessage(data, `Request failed with status ${response.status}`));
      error.status = response.status;
      error.payload = data;
      throw error;
    }

    return data;
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        login,
        register,
        setSession,
        logout,
        authFetch,
        authRequestJson,
        isAuthenticated: !!token,
        authReady,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}
